const SAFE_PROTOCOLS = new Set(['https:', 'http:']);

/**
 * Validates that an absolute URL uses a safe protocol (http or https).
 * Rejects javascript:, data:, vbscript:, ftp: and any other non-http(s) schemes.
 *
 * @param url - The absolute URL to validate
 * @returns true if the URL is valid and uses http: or https: protocol
 */
export function getIsSafeRedirectUrlProtocol(url: string): boolean {
    if (!url) return false;
    try {
        const parsed = new URL(url);
        return SAFE_PROTOCOLS.has(parsed.protocol);
    } catch {
        return false;
    }
}

/**
 * Validates that a target URL is a safe relative path for redirects.
 * Prevents open redirect vulnerabilities by ensuring the target is:
 * - A same-origin relative path starting with '/'
 * - Not a protocol-relative URL (starting with '//')
 * - Free from common header-injection characters
 *
 * @param target - The target URL to validate
 * @returns The validated target if safe, null otherwise
 *
 * @example
 * isSafeRelativeTarget('/dashboard') // returns '/dashboard'
 * isSafeRelativeTarget('//evil.com') // returns null
 * isSafeRelativeTarget('http://evil.com') // returns null
 * isSafeRelativeTarget('/path\nHeader: injected') // returns null
 */
export function isSafeRelativeTarget(target?: string): string | null {
    if (!target) return null;

    // Only allow same-origin relative paths like "/..."; disallow protocol-relative and absolute URLs
    if (!target.startsWith('/') || target.startsWith('//')) return null;

    // Root path is not a useful redirect target — let callers fall back to dashboard
    if (target === '/') return null;

    // Basic non-ASCII guard against header-injection weirdness
    if (/[^\x20-\x7E]/.test(target)) return null;

    return target;
}

const EXTERNAL_REDIRECT_PROTOCOL = 'https:';

/**
 * Validates an external partner redirect URL: absolute https URL without credentials.
 *
 * @param rawUrl - The decoded URL taken from the query string
 * @returns The original URL if valid, null otherwise
 */
export function getValidExternalRedirectUrl(rawUrl?: string | null): string | null {
    if (!rawUrl) return null;
    try {
        const parsed = new URL(rawUrl);
        const isValid = parsed.protocol === EXTERNAL_REDIRECT_PROTOCOL
            && !parsed.username
            && !parsed.password
            && Boolean(parsed.hostname);
        return isValid ? rawUrl : null;
    } catch {
        return null;
    }
}

/**
 * Adds transactionId and status query params to a redirect URL, keeping other params and the hash.
 *
 * @param redirectUrl - A URL already validated by getValidExternalRedirectUrl
 * @param transactionId - Id of the sent transaction
 * @param status - Transaction status to report
 * @returns The redirect URL with the params set
 */
export function getRedirectUrlWithTransactionResult(
    redirectUrl: string,
    transactionId: string,
    status: string
): string {
    const url = new URL(redirectUrl);
    url.searchParams.set('transactionId', transactionId);
    url.searchParams.set('status', status);
    return url.toString();
}
